// Registry of subtypes. Add a new subtype by appending a definition here — the editor builds
// its toolbox chip, preview and settings form from the definition alone.
import {
  Cake,
  Calendar,
  CalendarClock,
  Gift,
  Image,
  Images,
  LinkIcon,
  List,
  ListChecks,
  PartyPopper,
  Star,
  ThumbsUp,
  Type,
} from 'lucide-react'
import type { SubtypeDefinition } from '../types'
import { helpKey } from '../../help'
import { PropertyTypes, Subtype, type SubtypeProperty } from './types'
import { ToolboxItemScaffold } from '@/pages/action-modules/editor/subtypes-tab/ToolboxItemScaffold'
import { AnniversaryBonusPreview, BirthdayBonusPreview, ChecklistAnswerPreview, EvaluationStarsPreview, EvaluationThumbsPreview, LinkPreview, NoPreview, MultiPictureUploadPreview, PictureUploadPreview, TextAnswerPreview, DropdownAnswerPreview, DatePreview } from '@/pages/action-modules/editor/subtypes-tab/previews'
import { CelebratedAnniversariesEditor } from '@/pages/action-modules/editor/subtypes-tab/property-editors/CelebratedAnniversariesEditor'
import { ChecklistAnswerTypeEditor } from '@/pages/action-modules/editor/subtypes-tab/property-editors/ChecklistAnswerTypeEditor'
import { ChecklistItemsEditor } from '@/pages/action-modules/editor/subtypes-tab/property-editors/ChecklistItemsEditor'
import { ConditionSubtypeEditor } from '@/pages/action-modules/editor/subtypes-tab/property-editors/ConditionSubtypeEditor'
import { ConditionChecklistItemEditor } from '@/pages/action-modules/editor/subtypes-tab/property-editors/ConditionChecklistItemEditor'
import DatePropertyEditor from '@/pages/action-modules/editor/subtypes-tab/property-editors/DatePropertyEditor'
import { DefaultThumbsAnswerEditor } from '@/pages/action-modules/editor/subtypes-tab/property-editors/DefaultThumbsAnswerEditor'
import ServiceModuleIdEditor from '@/pages/action-modules/editor/subtypes-tab/property-editors/ServiceModuleIdEditor'
import {
  checklistItemsToEditorState,
  checklistItemsToModel,
  type EditorChecklistAnswerItem,
} from './checklist'

// New/seeded items carry `id: null` — the backend assigns real ids on save, and the null id
// marks them as unsaved so the condition editors can gate on it. `editorId` is the client-only
// row identity the items editor sorts by (see `checklist.ts`).
const defaultChecklistItems: EditorChecklistAnswerItem[] = [1, 2, 3].map((n, index) => ({
  id: null,
  text: `Auswahl ${n}`,
  positiveCheckedReward: 0,
  negativeCheckedReward: 0,
  notCheckedReward: 0,
  defaultCheckListAnswer: 'NotChecked',
  order: index,
  editorId: index
}))

export const subtypes: SubtypeDefinition[] = [
  {
    nameKey: 'subtypes.anniversaryBonus.name',
    helpKey: helpKey('subtypeAnniversaryBonus'),
    type: Subtype.AnniversaryBonusModuleSubtype,
    icon: <PartyPopper className="size-5" />,
    toolboxPlaceholder: t => <ToolboxItemScaffold icon={<PartyPopper className="size-5" />} name={t('subtypes.anniversaryBonus.name')} />,
    editorPreview: AnniversaryBonusPreview,
    editableProperties: [
      {
        name: 'celebratedAnniversaries',
        displayNameKey: 'subtypes.anniversaryBonus.celebratedAnniversaries',
        helpKey: helpKey('subtypeAnniversaryBonusCelebratedAnniversaries'),
        propertyType: PropertyTypes.complex,
        defaultValue: [5, 10, 15, 20, 25, 30],
        editor: props => <CelebratedAnniversariesEditor {...props} />
      },
      {
        name: 'bonusRewardPoints',
        displayNameKey: 'subtypes.anniversaryBonus.bonusReward',
        helpKey: helpKey('subtypeAnniversaryBonusBonusReward'),
        propertyType: PropertyTypes.integer,
        defaultValue: 600
      },
    ],
  },
  {
    nameKey: 'subtypes.birthdayBonus.name',
    helpKey: helpKey('subtypeBirthdayBonus'),
    type: Subtype.BirthdayBonusModuleSubtype,
    icon: <Cake className="size-5" />,
    toolboxPlaceholder: t => <ToolboxItemScaffold icon={<Cake className="size-5" />} name={t('subtypes.birthdayBonus.name')} />,
    editorPreview: BirthdayBonusPreview,
    editableProperties: [
      {
        name: 'bonusRewardPoints',
        displayNameKey: 'subtypes.birthdayBonus.bonusReward',
        helpKey: helpKey('subtypeBirthdayBonusBonusReward'),
        propertyType: PropertyTypes.integer,
        defaultValue: 600,
      },
    ],
  },
  {
    nameKey: 'subtypes.checklistAnswer.name',
    helpKey: helpKey('subtypeChecklistAnswer'),
    type: Subtype.ChecklistAnswerModuleSubtype,
    icon: <ListChecks className="size-5" />,
    toolboxPlaceholder: t => <ToolboxItemScaffold icon={<ListChecks className="size-5" />} name={t('subtypes.checklistAnswer.name')} />,
    editorPreview: ChecklistAnswerPreview,
    editableProperties: [
      {
        name: 'checklistAnswerType',
        displayNameKey: 'subtypes.checklistAnswer.checklistAnswerType',
        helpKey: helpKey('subtypeChecklistAnswerType'),
        propertyType: PropertyTypes.complex,
        defaultValue: 'SingleChoice',
        editor: props => <ChecklistAnswerTypeEditor {...props} />
      },
      {
        name: 'checklistItems',
        displayNameKey: 'subtypes.checklistAnswer.checklistItems',
        helpKey: helpKey('subtypeChecklistAnswerItems'),
        propertyType: PropertyTypes.complex,
        defaultValue: defaultChecklistItems,
        complexModelToEditorState: checklistItemsToEditorState,
        complexEditorStateToModel: checklistItemsToModel,
        editor: props => <ChecklistItemsEditor {...props} />
      },
    ],
  },
  {
    nameKey: 'subtypes.evaluationStars.name',
    helpKey: helpKey('subtypeEvaluationStars'),
    type: Subtype.EvaluationStarsModuleSubtype,
    icon: <Star className="size-5" />,
    toolboxPlaceholder: t => <ToolboxItemScaffold icon={<Star className="size-5" />} name={t('subtypes.evaluationStars.name')} />,
    editorPreview: EvaluationStarsPreview,
    editableProperties: [
      {
        name: 'defaultNumberOfStarsAnswer',
        displayNameKey: 'subtypes.evaluationStars.defaultNumberOfStarsAnswer',
        helpKey: helpKey('subtypeEvaluationStarsDefaultAnswer'),
        propertyType: PropertyTypes.integer,
      },
    ],
  },
  {
    nameKey: 'subtypes.evaluationThumbs.name',
    helpKey: helpKey('subtypeEvaluationThumbs'),
    type: Subtype.EvaluationThumbsModuleSubtype,
    icon: <ThumbsUp className="size-5" />,
    toolboxPlaceholder: t => <ToolboxItemScaffold icon={<ThumbsUp className="size-5" />} name={t('subtypes.evaluationThumbs.name')} />,
    editorPreview: EvaluationThumbsPreview,
    editableProperties: [
      {
        name: 'defaultThumbsAnswer',
        displayNameKey: 'subtypes.evaluationThumbs.defaultThumbsAnswer',
        helpKey: helpKey('subtypeEvaluationThumbsDefaultAnswer'),
        propertyType: PropertyTypes.complex,
        defaultValue: null,
        editor: props => <DefaultThumbsAnswerEditor {...props} />
      },
    ],
  },
  {
    nameKey: 'subtypes.link.name',
    helpKey: helpKey('subtypeLink'),
    type: Subtype.LinkModuleSubtype,
    icon: <LinkIcon className="size-5" />,
    toolboxPlaceholder: t => <ToolboxItemScaffold icon={<LinkIcon className="size-5" />} name={t('subtypes.link.name')} />,
    editorPreview: LinkPreview,
    editableProperties: [
      {
        name: 'buttonName',
        displayNameKey: 'subtypes.link.buttonName',
        helpKey: helpKey('subtypeLinkButtonName'),
        propertyType: PropertyTypes.text,
        defaultValue: '',
        translatable: true
      },
      {
        name: 'url',
        displayNameKey: 'subtypes.link.url',
        helpKey: helpKey('subtypeLinkUrl'),
        propertyType: PropertyTypes.text
      },
      {
        name: 'serviceModuleId',
        displayNameKey: 'subtypes.link.serviceModule',
        helpKey: helpKey('subtypeLinkServiceModule'),
        propertyType: PropertyTypes.complex,
        editor: props => <ServiceModuleIdEditor {...props} />
      },
      // Required by the backend but derived from the linked module (no picker yet) — persist-only.
      {
        name: 'serviceModuleType',
        displayNameKey: 'subtypes.link.serviceModuleType',
        propertyType: PropertyTypes.complex,
        defaultValue: null,
      },
    ],
  },
  {
    nameKey: 'subtypes.monthlyTaxFreeBenefit.name',
    helpKey: helpKey('subtypeMonthlyTaxFreeBenefit'),
    type: Subtype.MonthlyTaxFreeBenefitSubtype,
    icon: <Gift className="size-5" />,
    toolboxPlaceholder: t => <ToolboxItemScaffold icon={<Gift className="size-5" />} name={t('subtypes.monthlyTaxFreeBenefit.name')} />,
    editorPreview: NoPreview,
    editableProperties: [],
  },
  {
    nameKey: 'subtypes.multiPictureUpload.name',
    helpKey: helpKey('subtypeMultiPictureUpload'),
    type: Subtype.MultiPictureUploadModuleSubtype,
    icon: <Images className="size-5" />,
    toolboxPlaceholder: t => <ToolboxItemScaffold icon={<Images className="size-5" />} name={t('subtypes.multiPictureUpload.name')} />,
    editorPreview: MultiPictureUploadPreview,
    editableProperties: [
      {
        name: 'pictureDescription',
        displayNameKey: 'subtypes.multiPictureUpload.pictureDescription',
        helpKey: helpKey('subtypeMultiPictureUploadPictureDescription'),
        propertyType: PropertyTypes.text,
        translatable: true
      },
      {
        name: 'exampleImageUrl',
        displayNameKey: 'subtypes.multiPictureUpload.exampleImageUrl',
        helpKey: helpKey('subtypeMultiPictureUploadExampleImageUrl'),
        propertyType: PropertyTypes.text
      },
      {
        name: 'minimumRequiredPictures',
        displayNameKey: 'subtypes.multiPictureUpload.minimumRequiredPictures',
        helpKey: helpKey('subtypeMultiPictureUploadMinimumRequiredPictures'),
        propertyType: PropertyTypes.integer
      },
      {
        name: 'maximumAllowedPictures',
        displayNameKey: 'subtypes.multiPictureUpload.maximumAllowedPictures',
        helpKey: helpKey('subtypeMultiPictureUploadMaximumAllowedPictures'),
        propertyType: PropertyTypes.integer
      },
      {
        name: 'generateSummaryPdf',
        displayNameKey: 'subtypes.multiPictureUpload.generateSummaryPdf',
        helpKey: helpKey('subtypeMultiPictureUploadGenerateSummaryPdf'),
        propertyType: PropertyTypes.boolean
      },
    ],
  },
  {
    nameKey: 'subtypes.pictureUpload.name',
    helpKey: helpKey('subtypePictureUpload'),
    type: Subtype.PictureUploadModuleSubtype,
    icon: <Image className="size-5" />,
    toolboxPlaceholder: t => <ToolboxItemScaffold icon={<Image className="size-5" />} name={t('subtypes.pictureUpload.name')} />,
    editorPreview: PictureUploadPreview,
    editableProperties: [
      {
        name: 'pictureDescription',
        displayNameKey: 'subtypes.pictureUpload.pictureDescription',
        helpKey: helpKey('subtypePictureUploadPictureDescription'),
        propertyType: PropertyTypes.text,
        translatable: true
      },
      {
        name: 'exampleImageUrl',
        displayNameKey: 'subtypes.pictureUpload.exampleImageUrl',
        helpKey: helpKey('subtypePictureUploadExampleImageUrl'),
        propertyType: PropertyTypes.text
      },
    ],
  },
  {
    nameKey: 'subtypes.surveyAppointment.name',
    helpKey: helpKey('subtypeSurveyAppointment'),
    type: Subtype.SurveyAppointmentModuleSubtype,
    icon: <CalendarClock className="size-5" />,
    toolboxPlaceholder: t => <ToolboxItemScaffold icon={<CalendarClock className="size-5" />} name={t('subtypes.surveyAppointment.name')} />,
    editorPreview: NoPreview,
    editableProperties: [
      {
        name: 'calendarServiceModule',
        displayNameKey: 'subtypes.surveyAppointment.calendarServiceModule',
        helpKey: helpKey('subtypeSurveyAppointmentCalendarServiceModule'),
        propertyType: PropertyTypes.complex,
        editor: props => <ServiceModuleIdEditor {...props} />
      },
      {
        name: 'groupAppointments',
        displayNameKey: 'subtypes.surveyAppointment.groupAppointments',
        helpKey: helpKey('subtypeSurveyAppointmentGroupAppointments'),
        propertyType: PropertyTypes.boolean
      },
      {
        name: 'allowNoViableEvent',
        displayNameKey: 'subtypes.surveyAppointment.allowNoViableEvent',
        helpKey: helpKey('subtypeSurveyAppointmentAllowNoViableEvent'),
        propertyType: PropertyTypes.boolean
      },
      {
        name: 'allowReject',
        displayNameKey: 'subtypes.surveyAppointment.allowReject',
        helpKey: helpKey('subtypeSurveyAppointmentAllowReject'),
        propertyType: PropertyTypes.boolean
      },
      {
        name: 'allowOneEventPerCompletion',
        displayNameKey: 'subtypes.surveyAppointment.allowOneEventPerCompletion',
        helpKey: helpKey('subtypeSurveyAppointmentAllowOneEventPerCompletion'),
        propertyType: PropertyTypes.boolean
      },
    ],
  },
  {
    nameKey: 'subtypes.textAnswer.name',
    helpKey: helpKey('subtypeTextAnswer'),
    type: Subtype.TextAnswerModuleSubtype,
    icon: <Type className="size-5" />,
    toolboxPlaceholder: t => <ToolboxItemScaffold icon={<Type className="size-5" />} name={t('subtypes.textAnswer.name')} />,
    editorPreview: TextAnswerPreview,
    editableProperties: [
      {
        name: 'hintText',
        displayNameKey: 'subtypes.textAnswer.hintText',
        helpKey: helpKey('subtypeTextAnswerHintText'),
        propertyType: PropertyTypes.text,
        defaultValue: '',
        translatable: true
      },
      {
        name: 'defaultTextAnswer',
        displayNameKey: 'subtypes.textAnswer.defaultTextAnswer',
        helpKey: helpKey('subtypeTextAnswerDefaultTextAnswer'),
        propertyType: PropertyTypes.text,
        defaultValue: null,
      },
    ],
  },
  {
    nameKey: 'subtypes.dropdownAnswer.name',
    helpKey: helpKey('subtypeDropdownAnswer'),
    type: Subtype.DropdownAnswerModuleSubtype,
    icon: <List className="size-5" />,
    toolboxPlaceholder: t => <ToolboxItemScaffold icon={<List className="size-5" />} name={t('subtypes.dropdownAnswer.name')} />,
    editorPreview: DropdownAnswerPreview,
    editableProperties: [
      {
        name: 'checklistItems',
        displayNameKey: 'subtypes.dropdownAnswer.checklistItems',
        helpKey: helpKey('subtypeDropdownAnswerChecklistItems'),
        propertyType: PropertyTypes.complex,
        defaultValue: defaultChecklistItems,
        complexModelToEditorState: checklistItemsToEditorState,
        complexEditorStateToModel: checklistItemsToModel,
        editor: props => <ChecklistItemsEditor {...props} />
      },
      // Inherited required field on the backend's dropdown model (extends checklist); a dropdown is
      // single-choice and the editor doesn't expose the picker — persist-only default.
      {
        name: 'checklistAnswerType',
        displayNameKey: 'subtypes.dropdownAnswer.checklistAnswerType',
        propertyType: PropertyTypes.complex,
        defaultValue: 'SingleChoice',
        hidden: true
      },
    ],
  },
  {
    nameKey: 'subtypes.date.name',
    helpKey: helpKey('subtypeDate'),
    type: Subtype.DateModuleSubtype,
    icon: <Calendar className="size-5" />,
    toolboxPlaceholder: t => <ToolboxItemScaffold icon={<Calendar className="size-5" />} name={t('subtypes.date.name')} />,
    editorPreview: DatePreview,
    editableProperties: [
      {
        name: 'minDate',
        displayNameKey: 'subtypes.date.minDate',
        helpKey: helpKey('subtypeDateMinDate'),
        propertyType: PropertyTypes.complex,
        editor: props => <DatePropertyEditor {...props} />
      },
      {
        name: 'maxDate',
        displayNameKey: 'subtypes.date.maxDate',
        helpKey: helpKey('subtypeDateMaxDate'),
        propertyType: PropertyTypes.complex,
        editor: props => <DatePropertyEditor {...props} />
      },
      {
        name: 'autoFillWithCurrentDate',
        displayNameKey: 'subtypes.date.autoFillWithCurrentDate',
        helpKey: helpKey('subtypeDateAutoFillWithCurrentDate'),
        propertyType: PropertyTypes.boolean,
        defaultValue: true
      },
      {
        name: 'defaultDateAnswer',
        displayNameKey: 'subtypes.date.defaultDateAnswer',
        helpKey: helpKey('subtypeDateDefaultDateAnswer'),
        propertyType: PropertyTypes.complex,
        editor: props => <DatePropertyEditor {...props} />
      },
    ],
  },
]

// Base properties shared by every subtype (rendered before the definition-specific ones).
export const subtypeDefaultProperties: SubtypeProperty[] = [
  {
    name: 'isRequiredForCompletionByEmployee',
    displayNameKey: 'subtypes.base.isRequiredForCompletionByEmployee',
    helpKey: helpKey('subtypeIsRequiredForCompletionByEmployee'),
    propertyType: PropertyTypes.boolean,
    defaultValue: true
  },
  {
    name: 'isRequiredForCompletionByCompanyManagement',
    displayNameKey: 'subtypes.base.isRequiredForCompletionByCompanyManagement',
    helpKey: helpKey('subtypeIsRequiredForCompletionByCompanyManagement'),
    propertyType: PropertyTypes.boolean,
    defaultValue: false
  },
  {
    name: 'name',
    displayNameKey: 'subtypes.base.name',
    helpKey: helpKey('subtypeName'),
    propertyType: PropertyTypes.text,
    translatable: true
  },
  {
    name: 'randomizationGroup',
    displayNameKey: 'subtypes.base.randomizationGroup',
    helpKey: helpKey('subtypeRandomizationGroup'),
    propertyType: PropertyTypes.integer
  },
  {
    name: 'importColumnName',
    displayNameKey: 'subtypes.base.importColumnName',
    helpKey: helpKey('subtypeImportColumnName'),
    propertyType: PropertyTypes.text
  },
  {
    name: 'conditionSubtypeId',
    displayNameKey: 'subtypes.base.conditionSubtypeId',
    helpKey: helpKey('subtypeConditionSubtypeId'),
    propertyType: PropertyTypes.complex,
    defaultValue: null,
    editor: props => <ConditionSubtypeEditor {...props} />
  },
  {
    name: 'conditionChecklistItemId',
    displayNameKey: 'subtypes.base.conditionChecklistItemId',
    helpKey: helpKey('subtypeConditionChecklistItemId'),
    propertyType: PropertyTypes.complex,
    defaultValue: null,
    editor: props => <ConditionChecklistItemEditor {...props} />
  },
  {
    name: 'negateCondition',
    displayNameKey: 'subtypes.base.negateCondition',
    helpKey: helpKey('subtypeNegateCondition'),
    propertyType: PropertyTypes.boolean,
    defaultValue: false
  },
]

export const DefaultUnknownSubtypeDefinition: SubtypeDefinition = {
  nameKey: 'subtypes.unknown.name',
  // No backend `Unknown` discriminator exists; this sentinel only labels subtypes whose
  // persisted type the client build doesn't recognise (it is never sent back on save).
  type: 'Unknown' as unknown as Subtype,
  icon: <>?</>,
  toolboxPlaceholder: t => <>{t('subtypes.unknown.name')}</>,
  editorPreview: (_state, t) => <>{t('subtypes.unknown.preview')}</>,
  editableProperties: [],
}
